import { Injectable } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { serverTimestamp, setDoc } from 'firebase/firestore';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { AuthService } from '@auth';
import { toValidDate } from '@shared/helpers/convert-to-date';
import { FirestoreService } from './firestore.service';
import { KnownDeviceIdentityService } from './known-device-identity.service';
import { LoggingService } from './logging.service';
import { OnlineOfflineService } from './online-offine.service';

export const KNOWN_DEVICE_REVOCATIONS_COLLECTION = 'knownDeviceRevocations';

export type KnownDeviceRevocationReason = 'device-removed' | 'all-devices-removed';

/**
 * `knownDeviceRevocations/{tenantId}/devices/{deviceId}` — written by the administrator who removes a
 * known device, watched by every browser that resolved to that device. One document per device: a later
 * removal overwrites it with a newer `revokedAt`, so the document always states the latest removal.
 */
export interface KnownDeviceRevocation {
  deviceId: string;
  reason: KnownDeviceRevocationReason;
  /** Server time of the removal (`serverTimestamp()`); `null` in a snapshot taken before the server stamped it. */
  revokedAt: Timestamp | Date | null;
  revokedBy: { id: string; email: string };
}

export function knownDeviceRevocationPath(tenantId: string, deviceId: string): string {
  return `${KNOWN_DEVICE_REVOCATIONS_COLLECTION}/${tenantId}/devices/${deviceId}`;
}

/**
 * Whether a revocation ends the session that authenticated at `authTimeSeconds` (the access token's
 * `auth_time`). A removal ends every session that authenticated strictly BEFORE the second it was written,
 * and nothing that authenticated later — so a stale document can never sign out someone who signed in on
 * the same device again, and the same browser's tabs (one SSO session, one `auth_time`) agree with each
 * other. Compared at second granularity because `auth_time` carries no fraction. Without a usable
 * `auth_time` the answer is "no": failing open here is what rules out a sign-in → sign-out loop.
 */
export function revocationEndsSession(
  revocation: KnownDeviceRevocation | undefined,
  authTimeSeconds: number | undefined
): boolean {
  const revokedAt = toValidDate(revocation?.revokedAt);
  if (!revokedAt || authTimeSeconds === undefined) {
    return false;
  }
  return Math.floor(revokedAt.getTime() / 1000) > authTimeSeconds;
}

/**
 * Signs a removed known device out, entirely client-side (Bug 28014).
 *
 * Removing a device only deletes its registry row in the core API; nothing touches the Keycloak session,
 * and the access token lives for a day, so the removed device stayed signed in. Keycloak's admin API can't
 * be called from the browser (it needs realm-wide admin rights in a realm shared by every organization),
 * so the device signs ITSELF out instead:
 *
 *  - the administrator who removes a device writes a revocation document for it ([[revoke]]);
 *  - every signed-in browser watches the revocation document of the device it resolved to ([[watch]]) and,
 *    when a removal newer than its own session arrives, runs the normal logout — which is RP-initiated, so
 *    it ends the Keycloak SSO session for real (the refresh token dies, the SSO cookie is gone), not just
 *    the local copy of it.
 *
 * Firestore delivers the document live while the device is online and on the next start otherwise, so a
 * device that was closed or offline during the removal is signed out as soon as it comes back. The logout
 * itself is held while offline: the Keycloak redirect could not reach the server, and clearing only local
 * state would leave the SSO cookie alive to sign the device straight back in.
 */
@Injectable({
  providedIn: 'root'
})
export class KnownDeviceRevocationService {
  private watching = false;
  private deviceId: string | null = null;
  private readonly restart$ = new Subject<void>();
  /** A revocation that arrived while offline, acted on once the connection is back. */
  private pending?: KnownDeviceRevocation;

  constructor(
    private firestoreService: FirestoreService,
    private authService: AuthService,
    private knownDeviceIdentityService: KnownDeviceIdentityService,
    private onlineOfflineService: OnlineOfflineService,
    private loggingService: LoggingService
  ) {
    // Subscribed once for the service's lifetime: listen() restarts restart$-bound subscriptions.
    this.authService.tenantSwitched$.subscribe(() => {
      if (this.watching && this.deviceId) {
        this.listen(this.deviceId);
      }
    });
  }

  // --- administrator side -------------------------------------------------------------

  /**
   * Records that `deviceId` was removed, which signs out every session on it that is older than this
   * moment. Rejects when the write is refused; the caller decides how to report a device that was removed
   * from the list but could not be signed out.
   */
  async revoke(deviceId: string, reason: KnownDeviceRevocationReason): Promise<void> {
    const tenantId = this.authService.currentTenant;
    if (!tenantId || !deviceId) {
      throw new Error('[known-device-revocation] tenant and device are required to revoke a device');
    }

    const ref = this.firestoreService.doc<KnownDeviceRevocation>(knownDeviceRevocationPath(tenantId, deviceId));
    const revocation = {
      deviceId,
      reason,
      revokedAt: serverTimestamp(),
      revokedBy: { id: this.authService.currentUserId, email: this.authService.currentUserEmail },
    };

    await this.firestoreService.waitForAuthUser();
    await this.firestoreService.settleWrite(() => setDoc(ref, revocation as any), ref.path);
  }

  // --- signed-in device side ------------------------------------------------------------

  /**
   * Starts watching this device's revocation document for the signed-in session. Idempotent; call it
   * whenever the app becomes authenticated. Re-arms itself after a Firestore sign-out so the next
   * sign-in is covered too, and follows the current tenant across an organization switch.
   */
  watch(): void {
    if (this.watching) {
      return;
    }
    this.watching = true;

    this.start().catch((error) => {
      this.watching = false;
      console.warn('[known-device-revocation] could not watch for device removal', error);
    });
  }

  private async start(): Promise<void> {
    const { deviceId } = await this.knownDeviceIdentityService.resolveDevice();
    if (!deviceId) {
      // No device identity means this browser is not in the list either, so there is nothing to revoke.
      this.watching = false;
      return;
    }

    if (!(await this.firestoreService.waitForAuthUser())) {
      this.watching = false;
      return;
    }

    this.deviceId = deviceId;
    this.listen(deviceId);
  }

  private listen(deviceId: string): void {
    this.restart$.next();
    this.pending = undefined;

    const path = knownDeviceRevocationPath(this.authService.currentTenant, deviceId);

    // docValueChanges$ completes when Firestore signs out (logout / tenant teardown): the watch is over
    // until the next sign-in calls watch() again.
    this.firestoreService
      .docValueChanges$<KnownDeviceRevocation>(path)
      .pipe(takeUntil(this.restart$))
      .subscribe({
        next: (revocation) => this.onRevocation(revocation),
        complete: () => (this.watching = false),
      });

    this.onlineOfflineService.connectionChanged
      .pipe(filter((online) => online), takeUntil(this.restart$))
      .subscribe(() => {
        if (this.pending) {
          this.onRevocation(this.pending);
        }
      });

    this.watching = true;
  }

  private onRevocation(revocation: KnownDeviceRevocation | undefined): void {
    if (!revocation || !revocationEndsSession(revocation, this.authService.sessionAuthTime)) {
      return;
    }

    if (!this.onlineOfflineService.isOnline) {
      this.pending = revocation;
      return;
    }

    this.pending = undefined;
    this.loggingService.warning('Known device removed by an administrator - signing out', {
      DeviceId: revocation.deviceId,
      Reason: revocation.reason,
      RevokedBy: revocation.revokedBy?.email,
    });
    this.authService.logout('auth.deviceRemovedMessage');
  }
}
